// Constants
import { LearnMoreLinks } from "@ms/uno-constants/lib/local/AppConstants";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { Spinner } from "@fluentui/react-components";
import { SecureLink } from "@ms/planner-generic-controls/lib/local/secureLink/SecureLink";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { usePlanSettingsStyles } from "../PlanSettings.styles";
// Utilities
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as React from "react";
import { BackgroundOption } from "../backgroundOption/BackgroundOption";
/**
 * BackgroundPicker component for displaying and selecting plan background images
 *
 * This component handles three states:
 * - Loading: suggestions is null
 * - Error/No suggestions: suggestions is an empty array
 * - Success: suggestions contains image options
 *
 * @param props - Component properties
 * @returns JSX element representing the background picker interface
 */ export const BackgroundPicker = ({ suggestions, selectedUrl, canUpdateBackground, onSelect })=>{
    const styles = usePlanSettingsStyles();
    // Options shown in the success-state listbox: the default option plus up to 9 suggestions.
    const visibleSuggestions = suggestions?.slice(0, 9) ?? [];
    const optionCount = 1 + visibleSuggestions.length;
    // Index of the selected option (0 = default/no background, then suggestions in order).
    const selectedIndex = selectedUrl === null || selectedUrl === "" ? 0 : Math.max(0, visibleSuggestions.findIndex((s)=>s.fullUrl === selectedUrl) + 1) || 0;
    // Roving tabindex: only the active option is in the tab order; the rest are reachable via arrow keys.
    const [activeIndex, setActiveIndex] = React.useState(selectedIndex);
    const optionRefs = React.useRef([]);
    // Keep the tab stop on the selected option when selection changes elsewhere.
    // Adjust state during render (React-recommended) instead of a synchronizing effect.
    const [prevSelectedIndex, setPrevSelectedIndex] = React.useState(selectedIndex);
    if (prevSelectedIndex !== selectedIndex) {
        setPrevSelectedIndex(selectedIndex);
        setActiveIndex(selectedIndex);
    }
    // If the option list shrinks (e.g. suggestions reload with fewer results) the tab stop could
    // point past the last option, leaving every tile with tabIndex={-1} and unreachable via Tab.
    // Clamp it back into range during render.
    const [prevOptionCount, setPrevOptionCount] = React.useState(optionCount);
    if (prevOptionCount !== optionCount) {
        setPrevOptionCount(optionCount);
        if (activeIndex > optionCount - 1) {
            setActiveIndex(optionCount - 1);
        }
    }
    const setOptionRef = (index)=>(element)=>{
            optionRefs.current[index] = element;
        };
    // Count of tiles in the first grid row, derived from live layout since the grid is responsive.
    const getColumnCount = ()=>{
        const elements = optionRefs.current.filter((element)=>element != null);
        if (elements.length <= 1) {
            return 1;
        }
        const firstRowTop = elements[0].offsetTop;
        let columns = 0;
        for (const element of elements){
            if (element.offsetTop !== firstRowTop) {
                break;
            }
            columns++;
        }
        return Math.max(1, columns);
    };
    // Grid-aware roving-tabindex navigation: Left/Right step one tile, Up/Down move a full row.
    const handleListKeyDown = (e)=>{
        const key = KeyboardUtilities.getKey(e);
        let nextIndex = activeIndex;
        switch(key){
            case KeyCode.RIGHT_ARROW:
                nextIndex = Math.min(activeIndex + 1, optionCount - 1);
                break;
            case KeyCode.LEFT_ARROW:
                nextIndex = Math.max(activeIndex - 1, 0);
                break;
            case KeyCode.DOWN_ARROW:
                nextIndex = Math.min(activeIndex + getColumnCount(), optionCount - 1);
                break;
            case KeyCode.UP_ARROW:
                nextIndex = Math.max(activeIndex - getColumnCount(), 0);
                break;
            case KeyCode.HOME:
                nextIndex = 0;
                break;
            case KeyCode.END:
                nextIndex = optionCount - 1;
                break;
            default:
                return;
        }
        e.preventDefault();
        if (nextIndex !== activeIndex) {
            setActiveIndex(nextIndex);
            optionRefs.current[nextIndex]?.focus();
        }
    };
    // Loading state - suggestions haven't loaded yet
    if (suggestions === null) {
        return /*#__PURE__*/ React.createElement("div", {
            "data-testid": "background-picker-loading",
            className: styles.loadingState,
            role: "status"
        }, /*#__PURE__*/ React.createElement(Spinner, {
            size: "small"
        }), /*#__PURE__*/ React.createElement("div", {
            className: styles.loadingText
        }, Strings.PlanSettings_LoadingText));
    }
    // Empty state - no suggestions found (error) or user cannot update background
    if (!canUpdateBackground || suggestions.length === 0) {
        return /*#__PURE__*/ React.createElement("div", {
            "data-testid": "background-picker-empty",
            className: styles.emptyState
        }, /*#__PURE__*/ React.createElement("div", {
            className: styles.backgroundPicker,
            role: "listbox",
            "aria-label": Strings.PlanSettings_SuggestedBackgroundsLabel
        }, /*#__PURE__*/ React.createElement("div", {
            "data-testid": "background-picker-empty-default-option",
            className: `${!canUpdateBackground ? styles.backgroundOptionReadonly : styles.backgroundOption} ${selectedUrl === null ? styles.selected : ""}`,
            tabIndex: 0,
            role: "option",
            "aria-label": Strings.PlanSettings_NoBackgroundAriaLabel,
            "aria-selected": selectedUrl === null,
            "aria-disabled": !canUpdateBackground
        }, /*#__PURE__*/ React.createElement("div", {
            className: styles.backgroundThumbnail
        }, /*#__PURE__*/ React.createElement("svg", {
            className: styles.backgroundOverlayDefault,
            width: "244",
            height: "137",
            viewBox: "0 0 244 137",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            focusable: "false",
            "aria-hidden": "true"
        }, /*#__PURE__*/ React.createElement("rect", {
            x: "37.8457",
            y: "23.8584",
            width: "33.7318",
            height: "9.05",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "37.8457",
            y: "41.9585",
            width: "33.7318",
            height: "31.2636",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "37.8457",
            y: "82.272",
            width: "33.7318",
            height: "31.2636",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "82.2729",
            y: "23.8584",
            width: "33.7318",
            height: "9.05",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "82.2729",
            y: "41.9585",
            width: "33.7318",
            height: "18.9227",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "82.2729",
            y: "66.6401",
            width: "33.7318",
            height: "46.8954",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "126.7",
            y: "23.8584",
            width: "33.7318",
            height: "9.05",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "126.7",
            y: "41.9585",
            width: "33.7318",
            height: "31.2636",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "126.7",
            y: "82.272",
            width: "33.7318",
            height: "31.2636",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "171.127",
            y: "23.8584",
            width: "33.7318",
            height: "9.05",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "171.127",
            y: "41.9585",
            width: "33.7318",
            height: "18.9227",
            rx: "3",
            fill: "#efefef"
        }), /*#__PURE__*/ React.createElement("rect", {
            x: "171.127",
            y: "66.6401",
            width: "33.7318",
            height: "46.8954",
            rx: "3",
            fill: "#efefef"
        }))))), // Show error message only if user has permission to update background
        canUpdateBackground && /*#__PURE__*/ React.createElement("div", {
            className: styles.errorMessage
        }, /*#__PURE__*/ React.createElement("div", {
            className: styles.errorText
        }, Strings.PlanSettings_ImageErrorText)));
    }
    // Success state - has suggestions
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        "data-testid": "background-picker-suggestions",
        className: styles.backgroundPicker,
        role: "listbox",
        "aria-label": Strings.PlanSettings_SuggestedBackgroundsLabel,
        onKeyDown: handleListKeyDown
    }, /*#__PURE__*/ React.createElement("div", {
        "data-testid": "background-picker-default-option",
        ref: setOptionRef(0),
        className: `${styles.backgroundOption} ${selectedUrl === null || selectedUrl === "" ? styles.selected : ""}`,
        onClick: ()=>onSelect(""),
        onKeyDown: (e)=>{
            const key = KeyboardUtilities.getKey(e);
            if (key === KeyCode.ENTER || key === KeyCode.SPACE) {
                onSelect("");
                e.preventDefault();
            }
        },
        tabIndex: activeIndex === 0 ? 0 : -1,
        role: "option",
        "aria-label": Strings.PlanSettings_NoBackgroundAriaLabel,
        "aria-selected": selectedUrl === null || selectedUrl === ""
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.backgroundThumbnail
    }, /*#__PURE__*/ React.createElement("svg", {
        className: styles.backgroundOverlayDefault,
        width: "244",
        height: "137",
        viewBox: "0 0 244 137",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        focusable: "false",
        "aria-hidden": "true"
    }, /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "41.9585",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "37.8457",
        y: "82.272",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "41.9585",
        width: "33.7318",
        height: "18.9227",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "82.2729",
        y: "66.6401",
        width: "33.7318",
        height: "46.8954",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "41.9585",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "126.7",
        y: "82.272",
        width: "33.7318",
        height: "31.2636",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "23.8584",
        width: "33.7318",
        height: "9.05",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "41.9585",
        width: "33.7318",
        height: "18.9227",
        rx: "3",
        fill: "#efefef"
    }), /*#__PURE__*/ React.createElement("rect", {
        x: "171.127",
        y: "66.6401",
        width: "33.7318",
        height: "46.8954",
        rx: "3",
        fill: "#efefef"
    })))), visibleSuggestions.map((suggestion, index)=>/*#__PURE__*/ React.createElement(BackgroundOption, {
            key: suggestion.id,
            ref: setOptionRef(index + 1),
            suggestion: suggestion,
            index: index + 1,
            isSelected: selectedUrl === suggestion.fullUrl,
            tabIndex: activeIndex === index + 1 ? 0 : -1,
            onSelect: onSelect
        }))), Strings.PlanSettings_AIDesignerAttributionText && /*#__PURE__*/ React.createElement("div", {
        className: styles.aiDesignerAttribution
    }, /*#__PURE__*/ React.createElement(SecureLink, {
        href: LearnMoreLinks.microsoftDesigner,
        inline: false
    }, Strings.PlanSettings_AIDesignerAttributionText)));
};
